import { useCallback, useState, useRef, useContext, useEffect } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import webCallBtnShown from '@hh.ru/analytics-js-events/build/xhh/employer/calls/web_call/call_button_element_shown';
import webCallBtnClick from '@hh.ru/analytics-js-events/build/xhh/employer/calls/web_call/web_call_button_click';
import webCallTipShown from '@hh.ru/analytics-js-events/build/xhh/employer/calls/web_call/web_call_tip_element_shown';
import { ElementSpyInstance } from '@hh.ru/browser-api-utils';
import { Button, useBreakpoint, TooltipHover } from '@hh.ru/magritte-ui';
import { PhoneOutlinedSize16, PhoneCrossOutlinedSize16, LockClosedOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Notices, { MarkAsViewedEvent } from 'Modules/Notices';
import MagritteInfotip from 'src/components/Infotip/MagritteInfotip';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import WebcallWrapper from 'src/components/Webcall/WebcallWrapper';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';

import styles from './index.less';

const TrlKeys = {
    title: 'webcall.banner.title',
    description: 'webcall.banner.description',
    call: 'webcall.button.call',
    disabledStatus: 'webcall.status.disabled.title',
};

interface Props {
    isMobile?: boolean;
    topicId: string;
    resumeId: number;
}

const INFO_TIP_NAME = 'webcall-topic-button';

const CallButton: TranslatedComponent<Props> = ({ isMobile, trls, topicId, resumeId }) => {
    const showInfoTip = useSelector(({ infoTip }) => infoTip.name === INFO_TIP_NAME);
    const isWebcallLocked = useSelector(({ webcall }) => !webcall.stable);
    const isSudo = useSelector(({ actualAccount }) => actualAccount !== null);
    const refInfoTipBody = useRef(null);
    const lockedHoverTipRef = useRef(null);
    const { isFirstResponse } = useContext(ResumeCardActionsContext);
    const [infoTipVisible, setInfotipVisible] = useState(isFirstResponse && showInfoTip);
    const [hoverTipVisible, setHoverTipVisible] = useState(!infoTipVisible);

    useEffect(() => {
        if (infoTipVisible) {
            // так как в магрит тултипе нет события onSeen, ждем пока тултип появится и отправляем событие
            let spyInstance: ElementSpyInstance;
            const timeoutId = setTimeout(() => {
                if (refInfoTipBody.current) {
                    spyInstance = webCallTipShown(refInfoTipBody.current, {
                        resumeId,
                        hhtmSource: 'employer_vacancy_responses',
                    });
                }
            }, 1000);

            return () => {
                clearTimeout(timeoutId);
                spyInstance?.stopSpying();
            };
        }

        return undefined;
    }, [infoTipVisible, resumeId]);

    const { isS, isM } = useBreakpoint();

    const isButtonTextHidden = isS || isM;

    const [webcallShown, setWebcallShown] = useState(false);
    const widgetId = `webcall-front-${topicId}`;

    const tooltipText = `${trls[TrlKeys.title]}. ${trls[TrlKeys.description]}`;

    const handleClick = useCallback(() => {
        if (isSudo) {
            return;
        }
        setWebcallShown(true);
        setInfotipVisible(false);
        Notices.markAsViewed(INFO_TIP_NAME);
        // показываем ховер-тултип через некоторое время чтобы избежать "мигания" между тултипами
        setTimeout(() => {
            setHoverTipVisible(true);
        }, 3000);
        webCallBtnClick({
            resumeId,
            topicId,
            hhtmSource: 'employer_vacancy_responses',
        });
    }, [resumeId, topicId, isSudo]);

    const getMagritteIcon = () => {
        if (isWebcallLocked) {
            if (isButtonTextHidden) {
                return <PhoneCrossOutlinedSize16 />;
            }
            return <LockClosedOutlinedSize16 />;
        }
        return <PhoneOutlinedSize16 />;
    };

    return (
        <>
            <MagritteInfotip
                name={INFO_TIP_NAME}
                render={({ activatorRef }) => (
                    <div ref={lockedHoverTipRef}>
                        <span ref={activatorRef}>
                            <ElementShownAnchor fn={webCallBtnShown} resumeId={resumeId} topicId={topicId}>
                                <Button
                                    stretched={isMobile}
                                    size="small"
                                    mode="secondary"
                                    icon={getMagritteIcon()}
                                    onClick={handleClick}
                                    aria-label={trls[TrlKeys.call]}
                                    data-qa="webcall-call-btn"
                                    disabled={isWebcallLocked}
                                >
                                    {!isButtonTextHidden && trls[TrlKeys.call]}
                                </Button>
                            </ElementShownAnchor>
                            {activatorRef && hoverTipVisible && !isWebcallLocked && (
                                <TooltipHover placement="bottom-right" activatorRef={activatorRef}>
                                    {tooltipText}
                                </TooltipHover>
                            )}
                        </span>
                    </div>
                )}
                bannerStyle="constant-secondary"
                placement="bottom-right"
                showClose
                closeByClickOutside={false}
                visible={infoTipVisible}
                markAsViewedEvent={MarkAsViewedEvent.OnHide}
                onClose={() => {
                    setInfotipVisible(false);
                    setHoverTipVisible(true);
                }}
                useDefaultHost
            >
                <div ref={refInfoTipBody} className={styles.tooltipText}>
                    {tooltipText}
                </div>
            </MagritteInfotip>
            {isWebcallLocked && (
                <TooltipHover placement="top-center" activatorRef={lockedHoverTipRef} maxWidth={300}>
                    {trls[TrlKeys.disabledStatus]}
                </TooltipHover>
            )}
            {webcallShown && (
                <WebcallWrapper
                    place={widgetId}
                    onClose={() => setWebcallShown(false)}
                    hhtmSource="employer_vacancy_responses"
                    resumeId={resumeId}
                    topicId={topicId}
                />
            )}
        </>
    );
};

export default translation(CallButton);
